import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';

/** Dropdown with a search box and checkboxes. `value` is an array of selected option values. */
export default function MultiSelect({ options = [], value = [], onChange, placeholder = 'All', noun = 'selected', disabled, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const ref = useRef(null);
  const items = useMemo(() => options.map((o) => (typeof o === 'object' ? o : { value: o, label: o })), [options]);
  const visible = items.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()));

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const shown = value.length === 0 ? placeholder
    : value.length === 1 ? (items.find((o) => o.value === value[0]) || { label: value[0] }).label
      : `${value.length} ${noun}`;

  return (
    <div className="multi" ref={ref}>
      <button type="button" className={`input multi-trigger ${value.length ? 'has' : ''}`} disabled={disabled} aria-label={ariaLabel}
        aria-haspopup="listbox" aria-expanded={open} onClick={() => { setOpen(!open); setTerm(''); }}>
        <span className="grow">{shown}</span>
        {value.length > 0 && <span className="count">{value.length}</span>}
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="multi-panel" role="listbox" aria-multiselectable="true">
          {items.length > 8 && <input className="input" autoFocus placeholder="Search…" value={term} onChange={(e) => setTerm(e.target.value)} />}
          <div className="multi-actions">
            <button type="button" className="link" disabled={!visible.length}
              onClick={() => onChange([...new Set([...value, ...visible.map((o) => o.value)])])}>Select {term ? 'matches' : 'all'}</button>
            <button type="button" className="link" disabled={!value.length} onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="multi-list">
            {visible.map((o) => (
              <label key={o.value} className="check" role="option" aria-selected={value.includes(o.value)}>
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />{o.label}
              </label>
            ))}
            {visible.length === 0 && <div className="muted" style={{ padding: 8 }}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}
